import type { MagentoAccount } from "../../slices/accountsSlice";
import { sleep } from "../common";
import httpClient from "../common/httpClient";

// Frontend calls your Express backend;
// backend is responsible for Magento signing.

const backendBaseUrl =
  process.env.API_SERVER_BASE_URL || "http://localhost:4000";

const proxyBaseUrl = `${backendBaseUrl}/magento-api`;

function ensureBaseUrl() {
  if (!proxyBaseUrl) {
    throw new Error(
      "Set ADOBE_COMMERCE_BASE_URL to your Adobe Commerce host (e.g., https://store.example.com/rest)."
    );
  }
  return proxyBaseUrl.replace(/\/$/, "");
}

function ensureAccount(account?: MagentoAccount) {
  if (!account) {
    throw new Error("No Magento account selected.");
  }
  return account;
}

function buildAuthHeaders(account: MagentoAccount) {
  return {
    "x-magento-base-url": account.baseUrl,
    "x-magento-consumer-key": account.consumerKey,
    "x-magento-consumer-secret": account.consumerSecret,
    "x-magento-access-token": account.accessToken,
    "x-magento-access-token-secret": account.accessTokenSecret,
    "x-magento-store-code": account.storeCode || "",
  };
}

async function fetchProductsWithRetry(
  account: MagentoAccount,
  pageSize: number,
  page: number,
  retries = 5,
  baseDelay = 1000 // 1s
) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fetchProducts(account, pageSize, page);
    } catch (err: any) {
      const status = err?.status || err?.response?.status;

      if (status === 429 && attempt < retries) {
        const retryAfterHeader = err?.response?.headers?.["retry-after"];
        const retryAfter = Number(retryAfterHeader) * 1000 || 0;

        const delay = retryAfter || baseDelay * 2 ** (attempt - 1);

        console.warn(`429 Too Many Requests → retry ${attempt}/${retries} after ${delay}ms`);

        await sleep(delay);
        continue;
      }

      throw err; // non-429 or retries exhausted
    }
  }
  throw new Error(`Failed to fetch products after ${retries} retries`);
}

export async function fetchProducts(account: MagentoAccount, pageSize = Number(process.env.SYNC_PAGE_SIZE) || 100, currentPage = 1) {
  const normalizedBase = ensureBaseUrl();
  const resolvedAccount = ensureAccount(account);
  const query = new URLSearchParams({
    "searchCriteria[pageSize]": String(pageSize),
    "searchCriteria[currentPage]": String(currentPage),
  });
  const url = `${normalizedBase}/products?${query.toString()}`;
  const response = await httpClient.get(url, {
    headers: buildAuthHeaders(resolvedAccount),
  });

  return {
    items: response.data?.items ?? [],
    totalCount: response.data?.total_count ?? 0,
  };
}

export async function fetchAllProducts(account: MagentoAccount, pageSize = Number(process.env.SYNC_PAGE_SIZE) || 100) {
  const products: any[] = [];
  const resolvedAccount = ensureAccount(account);
  let page = 1;
  let tryCountm = 3;
  const safePageSize = pageSize > 0 ? pageSize : (Number(process.env.SYNC_PAGE_SIZE) || 100);
  while (tryCountm--) {
    const { items } = await fetchProductsWithRetry(resolvedAccount, safePageSize, page);
    if (!items?.length) break;
    products.push(...items);
    if (items.length < safePageSize) {
      break; // last page
    }

    page++;
  }

  return products;
}

export async function fetchSingleProduct(account: MagentoAccount, sku: string) {
  const normalizedBase = ensureBaseUrl();
  const resolvedAccount = ensureAccount(account);

  const url = `${normalizedBase}/product/${encodeURIComponent(sku)}`;
  const response = await httpClient.get(url, {
    headers: buildAuthHeaders(resolvedAccount),
  });

  return response.data;
}

export async function upsertProduct(
  account: MagentoAccount,
  product: Record<string, any>,
  method: "POST" | "PUT"
) {
  const normalizedBase = ensureBaseUrl();
  const resolvedAccount = ensureAccount(account);
  const url = `${normalizedBase}/products/upsert`;
  const response = await httpClient.post(
    url,
    { product, method },
    { headers: buildAuthHeaders(resolvedAccount) }
  );
  return response.data;
}
